import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, Search, AlertTriangle, Heart, Camera, User } from 'lucide-react'
import { FormSection } from '../ui/FormSection'
import { Button } from '../ui/Button'
import { validateDocumentId } from '../../lib/validateDocument'
import api from '../../lib/api'
import toast from 'react-hot-toast'

interface User {
  id: string
  firstName: string
  lastName: string
}

interface PatientResult {
  id: string
  firstName: string
  lastName: string
  patientCode: string
}

interface CreatePatientModalProps {
  onClose: () => void
  onSubmit: (data: any) => void
  isPending: boolean
  dentists: User[]
}

const inputClass = 'w-full bg-surface-0 border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-primary-400'
const labelClass = 'block text-xs font-medium text-text-secondary mb-1'
const textareaClass = `${inputClass} resize-none`

const MARITAL_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'single', label: 'Soltero/a' },
  { value: 'married', label: 'Casado/a' },
  { value: 'divorced', label: 'Divorciado/a' },
  { value: 'widowed', label: 'Viudo/a' },
  { value: 'separated', label: 'Separado/a' },
  { value: 'other', label: 'Otro' },
]

const PAYMENT_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'direct_debit', label: 'Domiciliacion bancaria' },
]

const CHANNEL_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'phone', label: 'Telefono' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  // WhatsApp oculto hasta tener WABA API
  // { value: 'whatsapp', label: 'WhatsApp' },
]

const GENDER_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'MALE', label: 'Masculino' },
  { value: 'FEMALE', label: 'Femenino' },
  { value: 'OTHER', label: 'Otro' },
]

const BLOOD_TYPE_OPTIONS = [
  { value: '', label: 'Desconocido' },
  { value: 'A+', label: 'A+' },
  { value: 'A-', label: 'A-' },
  { value: 'B+', label: 'B+' },
  { value: 'B-', label: 'B-' },
  { value: 'AB+', label: 'AB+' },
  { value: 'AB-', label: 'AB-' },
  { value: 'O+', label: 'O+' },
  { value: 'O-', label: 'O-' },
]

const SMOKING_OPTIONS = [
  { value: '', label: 'No fuma' },
  { value: 'occasional', label: 'Ocasional' },
  { value: 'light', label: 'Ligero (<10/dia)' },
  { value: 'moderate', label: 'Moderado (10-20/dia)' },
  { value: 'heavy', label: 'Grave (>20/dia)' },
  { value: 'former', label: 'Ex-fumador' },
]

const initialForm = {
  firstName: '', lastName: '', nickname: '',
  gender: '', birthDate: '', documentId: '',
  primaryDentistId: '', preferredLanguage: 'es',
  responsibleName: '', responsiblePhone: '',
  referralSource: '', referralPatientId: '', referralExternalName: '',
  phone: '', phone2: '', email: '', preferredChannel: '',
  address: '', postalCode: '', city: '', province: '',
  nationality: 'ES', profession: '', maritalStatus: '',
  billingName: '', billingDocument: '', billingAddress: '',
  billingPostalCode: '', billingCity: '', billingProvince: '', billingEmail: '',
  paymentMethod: '', iban: '',
  alertText: '', notes: '',
}

const initialMedicalHistory = {
  allergies: '',
  currentMedications: '',
  medicalConditions: '',
  bloodType: '',
  smoking: '',
  pregnancy: false,
  anticoagulants: false,
  anticoagulantDetails: '',
  bisphosphonates: false,
  cardiacProsthesis: false,
  diabetes: false,
  hypertension: false,
  heartDisease: false,
  hepatitis: false,
}

export function CreatePatientModal({ onClose, onSubmit, isPending, dentists }: CreatePatientModalProps) {
  const [form, setForm] = useState(initialForm)
  const [medicalHistory, setMedicalHistory] = useState(initialMedicalHistory)
  const [rgpdConsent, setRgpdConsent] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [shareDataWithInsurers, setShareDataWithInsurers] = useState(false)
  const [referralSearch, setReferralSearch] = useState('')
  const [showReferralResults, setShowReferralResults] = useState(false)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)

  const handlePhotoChange = (file: File | null) => {
    if (!file) {
      setPhotoPreview(null)
      set('photoUrl', '')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La foto no puede superar 2MB')
      return
    }
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result as string
      setPhotoPreview(result)
      set('photoUrl', result)
    }
    reader.readAsDataURL(file)
  }

  const set = (field: string, value: string) => setForm((f) => ({ ...f, [field]: value }))
  const setMedical = (field: string, value: string | boolean) => setMedicalHistory((m) => ({ ...m, [field]: value }))

  const age = form.birthDate
    ? Math.floor((Date.now() - new Date(form.birthDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null
  const isMinor = age !== null && age < 14

  const { data: referralPatients } = useQuery<PatientResult[]>({
    queryKey: ['patients-search', referralSearch],
    queryFn: async () => {
      const { data } = await api.get('/patients', { params: { search: referralSearch } })
      return data
    },
    enabled: referralSearch.length >= 2 && form.referralSource === 'INTERNAL',
  })

  const selectedReferralName = useMemo(() => {
    if (!form.referralPatientId || !referralPatients) return ''
    const p = referralPatients.find((r) => r.id === form.referralPatientId)
    return p ? `[${p.patientCode}] ${p.firstName} ${p.lastName}` : ''
  }, [form.referralPatientId, referralPatients])

  const documentValidation = validateDocumentId(form.documentId)
  const canSubmit = form.firstName.trim() && form.lastName.trim() && rgpdConsent &&
    (!isMinor || (form.responsibleName.trim() && form.responsiblePhone.trim())) &&
    (!form.documentId || documentValidation.valid)

  const handleSubmit = () => {
    const mh = { ...medicalHistory }
    // Clean up empty strings
    if (!mh.allergies) delete (mh as Record<string, unknown>).allergies
    if (!mh.currentMedications) delete (mh as Record<string, unknown>).currentMedications
    if (!mh.medicalConditions) delete (mh as Record<string, unknown>).medicalConditions
    if (!mh.bloodType) delete (mh as Record<string, unknown>).bloodType
    if (!mh.smoking) delete (mh as Record<string, unknown>).smoking
    if (!mh.anticoagulantDetails) delete (mh as Record<string, unknown>).anticoagulantDetails

    onSubmit({
      ...form,
      medicalHistory: Object.keys(mh).length > 0 ? mh : undefined,
      rgpdConsent,
      marketingConsent,
      shareDataWithInsurers,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-surface-1 border border-border rounded-xl w-full max-w-3xl shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h2 className="text-base font-semibold text-text-primary">Nuevo paciente</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-2 text-text-tertiary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-1">
          {/* Datos Principales */}
          <FormSection title="Datos Principales" defaultOpen>
            {/* Photo upload */}
            <div className="md:col-span-2"
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-primary-50/50') }}
              onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-primary-50/50') }}
              onDrop={(e) => {
                e.preventDefault()
                e.currentTarget.classList.remove('bg-primary-50/50')
                const file = e.dataTransfer.files?.[0]
                if (file) handlePhotoChange(file)
              }}
            >
              <label className={labelClass}>Foto del paciente</label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  {photoPreview ? (
                    <div className="relative">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="w-16 h-16 rounded-full object-cover border-2 border-primary-200"
                      />
                      <button
                        onClick={() => handlePhotoChange(null)}
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-error-500 text-white flex items-center justify-center text-xs hover:bg-error-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-400 flex items-center justify-center">
                      <User className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-surface-0 border border-border hover:bg-surface-2 cursor-pointer transition-colors text-sm text-text-secondary">
                    <Camera className="w-4 h-4" />
                    {photoPreview ? 'Cambiar foto' : 'Subir foto'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      className="sr-only"
                      onChange={(e) => handlePhotoChange(e.target.files?.[0] || null)}
                    />
                  </label>
                  <p className="text-[11px] text-text-tertiary mt-1">JPEG o PNG, max 2MB. Arrastra o haz clic.</p>
                </div>
              </div>
            </div>
            <div>
              <label className={labelClass}>Nombre *</label>
              <input required value={form.firstName} onChange={(e) => set('firstName', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Apellidos *</label>
              <input required value={form.lastName} onChange={(e) => set('lastName', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Nombre de pila</label>
              <input value={form.nickname} onChange={(e) => set('nickname', e.target.value)} className={inputClass} placeholder="Apodo o nombre corto" />
            </div>
            <div>
              <label className={labelClass}>Sexo</label>
              <select value={form.gender} onChange={(e) => set('gender', e.target.value)} className={inputClass}>
                {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Fecha de nacimiento</label>
              <input type="date" value={form.birthDate} onChange={(e) => set('birthDate', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>NIF / NIE</label>
              <input
                value={form.documentId}
                onChange={(e) => set('documentId', e.target.value.toUpperCase())}
                className={`${inputClass} ${form.documentId && !validateDocumentId(form.documentId).valid ? 'border-error-400 focus:border-error-500' : ''}`}
                placeholder="12345678A o X1234567A"
              />
              {form.documentId && (
                (() => {
                  const v = validateDocumentId(form.documentId)
                  if (v.valid) {
                    return (
                      <p className="text-[11px] text-success-600 mt-1">
                        {v.type === 'DNI' ? 'DNI valido' : v.type === 'NIE' ? 'NIE valido' : 'Documento valido'}
                      </p>
                    )
                  }
                  return <p className="text-[11px] text-error-500 mt-1">{v.message}</p>
                })()
              )}
              {!form.documentId && (
                <p className="text-[11px] text-text-tertiary mt-1">
                  Se recomienda introducir el DNI/NIE para identificacion correcta
                </p>
              )}
            </div>
            {isMinor && (
              <>
                <div className="md:col-span-2 bg-warning-50 border border-warning-200 rounded-lg p-3 text-xs text-warning-700">
                  El paciente es menor de 14 anos. Se requiere datos del tutor legal (LOPDGDD Art. 7).
                </div>
                <div>
                  <label className={labelClass}>Nombre del tutor *</label>
                  <input required value={form.responsibleName} onChange={(e) => set('responsibleName', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Telefono del tutor *</label>
                  <input type="tel" required value={form.responsiblePhone} onChange={(e) => set('responsiblePhone', e.target.value)} className={inputClass} />
                </div>
              </>
            )}
            <div>
              <label className={labelClass}>Profesional asignado</label>
              <select value={form.primaryDentistId} onChange={(e) => set('primaryDentistId', e.target.value)} className={inputClass}>
                <option value="">Seleccionar...</option>
                {dentists.map((d) => <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Idioma</label>
              <select value={form.preferredLanguage} onChange={(e) => set('preferredLanguage', e.target.value)} className={inputClass}>
                <option value="es">Castellano</option>
                <option value="ca">Catala</option>
                <option value="en">English</option>
              </select>
            </div>
          </FormSection>

          {/* Referidores */}
          <FormSection title="Referidores">
            <div>
              <label className={labelClass}>Tipo de referido</label>
              <select value={form.referralSource} onChange={(e) => { set('referralSource', e.target.value); set('referralPatientId', ''); set('referralExternalName', '') }} className={inputClass}>
                <option value="">Ninguno</option>
                <option value="INTERNAL">Interno (otro paciente)</option>
                <option value="EXTERNAL">Externo</option>
              </select>
            </div>
            {form.referralSource === 'INTERNAL' && (
              <div className="relative">
                <label className={labelClass}>Paciente que refiere</label>
                {form.referralPatientId ? (
                  <div className="flex items-center gap-2">
                    <span className="flex-1 text-sm text-text-primary bg-surface-0 border border-border rounded-lg px-3 py-2">{selectedReferralName}</span>
                    <button type="button" onClick={() => set('referralPatientId', '')} className="p-1 text-text-tertiary hover:text-error-600"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                      <input
                        value={referralSearch}
                        onChange={(e) => { setReferralSearch(e.target.value); setShowReferralResults(true) }}
                        onFocus={() => setShowReferralResults(true)}
                        placeholder="Buscar paciente..."
                        className={`${inputClass} pl-9`}
                      />
                    </div>
                    {showReferralResults && referralPatients && referralPatients.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-surface-1 border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {referralPatients.slice(0, 8).map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => { set('referralPatientId', p.id); setShowReferralResults(false); setReferralSearch('') }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-surface-2 text-text-primary"
                          >
                            <span className="text-text-tertiary mr-1">[{p.patientCode}]</span>
                            {p.firstName} {p.lastName}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {form.referralSource === 'EXTERNAL' && (
              <div>
                <label className={labelClass}>Nombre del referidor</label>
                <input value={form.referralExternalName} onChange={(e) => set('referralExternalName', e.target.value)} className={inputClass} placeholder="Nombre o entidad" />
              </div>
            )}
          </FormSection>

          {/* Datos de Contacto */}
          <FormSection title="Datos de Contacto">
            <div>
              <label className={labelClass}>Telefono principal</label>
              <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Telefono secundario</label>
              <input type="tel" value={form.phone2} onChange={(e) => set('phone2', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Correo electronico</label>
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Preferencia de contacto</label>
              <select value={form.preferredChannel} onChange={(e) => set('preferredChannel', e.target.value)} className={inputClass}>
                {CHANNEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </FormSection>

          {/* Datos Postales */}
          <FormSection title="Datos Postales">
            <div className="md:col-span-2">
              <label className={labelClass}>Direccion</label>
              <input value={form.address} onChange={(e) => set('address', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Codigo postal</label>
              <input value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Poblacion</label>
              <input value={form.city} onChange={(e) => set('city', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Provincia</label>
              <input value={form.province} onChange={(e) => set('province', e.target.value)} className={inputClass} />
            </div>
          </FormSection>

          {/* Otros datos */}
          <FormSection title="Otros datos de interes">
            <div>
              <label className={labelClass}>Nacionalidad</label>
              <input value={form.nationality} onChange={(e) => set('nationality', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Profesion</label>
              <input value={form.profession} onChange={(e) => set('profession', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Estado civil</label>
              <select value={form.maritalStatus} onChange={(e) => set('maritalStatus', e.target.value)} className={inputClass}>
                {MARITAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </FormSection>

          {/* Facturacion */}
          <FormSection title="Datos de Facturacion">
            <div>
              <label className={labelClass}>Nombre facturacion</label>
              <input value={form.billingName} onChange={(e) => set('billingName', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>NIF / CIF</label>
              <input value={form.billingDocument} onChange={(e) => set('billingDocument', e.target.value)} className={inputClass} />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Direccion fiscal</label>
              <input value={form.billingAddress} onChange={(e) => set('billingAddress', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Codigo postal</label>
              <input value={form.billingPostalCode} onChange={(e) => set('billingPostalCode', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Poblacion</label>
              <input value={form.billingCity} onChange={(e) => set('billingCity', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Provincia</label>
              <input value={form.billingProvince} onChange={(e) => set('billingProvince', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Email de facturacion</label>
              <input type="email" value={form.billingEmail} onChange={(e) => set('billingEmail', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Forma de pago</label>
              <select value={form.paymentMethod} onChange={(e) => set('paymentMethod', e.target.value)} className={inputClass}>
                {PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>IBAN</label>
              <input value={form.iban} onChange={(e) => set('iban', e.target.value)} className={inputClass} placeholder="ES00 0000 0000 0000 0000 0000" />
            </div>
          </FormSection>

          {/* Observaciones */}
          <FormSection title="Observaciones">
            <div className="md:col-span-2">
              <label className={labelClass}>Texto alerta (visible en ficha del paciente)</label>
              <input value={form.alertText} onChange={(e) => set('alertText', e.target.value)} className={`${inputClass} bg-emerald-50 border-emerald-200 text-emerald-800 placeholder:text-emerald-400`} placeholder="Ej: Mujer de Jose Vicente Queralt" />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Notas internas</label>
              <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} className={inputClass} />
            </div>
          </FormSection>

          {/* Historial Medico */}
          <FormSection title="Historial Medico">
            <div className="md:col-span-2">
              <label className={labelClass}>Alergias</label>
              <textarea
                value={medicalHistory.allergies}
                onChange={(e) => setMedical('allergies', e.target.value)}
                rows={2}
                placeholder="Ej: Penicilina, latex, anestesia..."
                className={textareaClass}
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Medicacion actual</label>
              <textarea
                value={medicalHistory.currentMedications}
                onChange={(e) => setMedical('currentMedications', e.target.value)}
                rows={2}
                placeholder="Ej: Sintrom, Metformina, Enalapril..."
                className={textareaClass}
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Condiciones medicas</label>
              <textarea
                value={medicalHistory.medicalConditions}
                onChange={(e) => setMedical('medicalConditions', e.target.value)}
                rows={2}
                placeholder="Ej: Asma, epilepsia, anemia..."
                className={textareaClass}
              />
            </div>
            <div>
              <label className={labelClass}>Grupo sanguineo</label>
              <select
                value={medicalHistory.bloodType}
                onChange={(e) => setMedical('bloodType', e.target.value)}
                className={inputClass}
              >
                {BLOOD_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Fumador</label>
              <select
                value={medicalHistory.smoking}
                onChange={(e) => setMedical('smoking', e.target.value)}
                className={inputClass}
              >
                {SMOKING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {form.gender === 'FEMALE' && (
              <label className="flex items-center gap-2 p-2.5 rounded-lg border border-border bg-surface-0 cursor-pointer hover:bg-surface-1 transition-colors">
                <input
                  type="checkbox"
                  checked={medicalHistory.pregnancy}
                  onChange={(e) => setMedical('pregnancy', e.target.checked)}
                  className="w-4 h-4 rounded border-border text-primary-600"
                />
                <span className="text-sm text-text-secondary">Embarazo</span>
              </label>
            )}

            {/* Critical conditions */}
            <div className="md:col-span-2 mt-2">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Heart className="w-3.5 h-3.5 text-error-500" />
                Condiciones criticas
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { key: 'anticoagulants', label: 'Anticoagulantes', critical: true },
                  { key: 'bisphosphonates', label: 'Bifosfonatos', critical: true },
                  { key: 'cardiacProsthesis', label: 'Protesis cardiaca', critical: true },
                  { key: 'diabetes', label: 'Diabetes' },
                  { key: 'hypertension', label: 'Hipertension' },
                  { key: 'heartDisease', label: 'Cardiopatia' },
                  { key: 'hepatitis', label: 'Hepatitis' },
                ].map((item) => {
                  const checked = !!medicalHistory[item.key as keyof typeof medicalHistory]
                  return (
                    <label
                      key={item.key}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                        checked
                          ? item.critical
                            ? 'border-error-300 bg-error-50'
                            : 'border-primary-300 bg-primary-50'
                          : 'border-border bg-surface-0 hover:bg-surface-1'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setMedical(item.key, e.target.checked)}
                        className="w-4 h-4 rounded border-border text-primary-600"
                      />
                      <span className={`text-xs font-medium ${
                        checked
                          ? item.critical ? 'text-error-700' : 'text-primary-700'
                          : 'text-text-secondary'
                      }`}>
                        {item.label}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>

            {medicalHistory.anticoagulants && (
              <div className="md:col-span-2">
                <div className="bg-error-50 border border-error-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-error-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-error-700">Paciente en tratamiento anticoagulante</p>
                    <input
                      value={medicalHistory.anticoagulantDetails}
                      onChange={(e) => setMedical('anticoagulantDetails', e.target.value)}
                      placeholder="Ej: Sintrom 4mg, INR objetivo 2-3"
                      className={`${inputClass} mt-1.5 bg-white border-error-200 text-error-800 placeholder:text-error-300`}
                    />
                  </div>
                </div>
              </div>
            )}
          </FormSection>

          {/* RGPD */}
          <FormSection title="RGPD" defaultOpen>
            <div className="md:col-span-2 space-y-3">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={rgpdConsent} onChange={(e) => setRgpdConsent(e.target.checked)} className="w-4 h-4 mt-0.5 rounded border-border text-primary-600 focus:ring-primary-300" />
                <span className="text-xs text-text-secondary leading-relaxed">
                  Consiento el tratamiento de mis datos personales para la gestion de pacientes y citas conforme a la{' '}
                  <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">politica de privacidad</a>. *
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="w-4 h-4 mt-0.5 rounded border-border text-primary-600 focus:ring-primary-300" />
                <span className="text-xs text-text-secondary leading-relaxed">
                  Consiento el envio de comunicaciones comerciales por cualquier medio (telefono, email, SMS) para felicitaciones, fiestas o fines promocionales.
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={shareDataWithInsurers} onChange={(e) => setShareDataWithInsurers(e.target.checked)} className="w-4 h-4 mt-0.5 rounded border-border text-primary-600 focus:ring-primary-300" />
                <span className="text-xs text-text-secondary leading-relaxed">
                  Consiento la cesion de mis datos a especialistas medicos y laboratorios para la prestacion de servicios sanitarios.
                </span>
              </label>
            </div>
          </FormSection>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border flex-shrink-0">
          <Button variant="tertiary" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" onClick={handleSubmit} disabled={isPending || !canSubmit}>
            Crear paciente
          </Button>
        </div>
      </div>
    </div>
  )
}
